import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Leaf, Sparkles, CheckCircle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import "../styles/emergence.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmergencePaymentSuccess() {
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [activated, setActivated] = useState(false);

  const isFrench = i18n.language === 'fr';
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (sessionId) {
      activatePremium();
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const activatePremium = async () => {
    try {
      const response = await axios.post(`${API}/emergence/premium/activate?session_id=${sessionId}`);
      if (response.data.status === "activated" || response.data.status === "already_activated") {
        setActivated(true);
      }
    } catch (error) {
      console.error("Error activating premium:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="emergence-bg min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#3B7A8C] animate-spin mx-auto mb-4" />
          <p className="text-[#7A8B99]">{isFrench ? "Activation en cours..." : "Activating..."}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="emergence-bg min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="emergence-card bg-white p-10 rounded-3xl">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#8FB8A8] to-[#3B7A8C] flex items-center justify-center mx-auto mb-6">
            {activated ? (
              <CheckCircle className="w-10 h-10 text-white" />
            ) : (
              <Sparkles className="w-10 h-10 text-white" />
            )}
          </div>

          <h1 className="font-heading text-2xl font-bold text-[#2D5A66] mb-3">
            {activated 
              ? (isFrench ? "Bienvenue dans Premium ! 🌱" : "Welcome to Premium! 🌱")
              : (isFrench ? "Paiement réussi !" : "Payment successful!")}
          </h1>

          <p className="text-[#7A8B99] mb-8">
            {isFrench 
              ? "Tu as maintenant accès à tous les parcours guidés de 21 jours, au chat illimité avec ton coach, et au journal intelligent complet."
              : "You now have access to all 21-day guided journeys, unlimited coach chat, and the complete smart journal."}
          </p>

          <div className="space-y-3">
            <Link to="/emergence/parcours" className="block">
              <Button className="w-full emergence-btn-primary">
                {isFrench ? "Découvrir les parcours" : "Discover journeys"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/emergence" className="block">
              <Button variant="outline" className="w-full rounded-full border-[#3B7A8C]/20 text-[#7A8B99]">
                {isFrench ? "Retour à l'accueil" : "Back to home"}
              </Button>
            </Link>
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-8">
          <Leaf className="w-5 h-5 text-[#3B7A8C]" />
          <span className="font-heading text-[#2D5A66]">Épanoui</span>
        </div>
      </div>
    </div>
  );
}
